import React, { Dispatch, useEffect, useState } from 'react';
import ZiChart, { ZiChartProps } from './ZiChart';
import { Chart, TChart } from '../../api/charts/Chart';
import { AlertVariant } from '@patternfly/react-core';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib';
import Loader from '../util/Loader';
import { Dataframe, TNewDataframe, TNewDataframeData } from '../../api/dataframes/Dataframes';
import {
	DataframeDataRetrievalRequest,
	DataframeDataRetrievalResponse,
	DimSettings,
	InsightsActionsRequest,
	InsightsActionsResponse,
	TDataframe,
	TDateRange,
} from '../../api/types';
import { formatCsvDataToColumns, MultipartResponse } from '../../helpers/multipart-response.helper';
import { Link, useParams } from 'react-router-dom';
import { useApplication, useUser } from '../user/ApplicationProvider';
import { ChartSeriesChartTypes } from '../../types/charts/chart-options';
import { TUnitType } from '../../api/analytics/UnitType';
import { TNewDateRange } from '../../api/types/TNewDateRange';
import { DateRange } from '../../api/date-period-selector/DateRange';
import { DashboardFilter } from '../../api/dashbboards/DashboardFilter';
import { ZiChartSettings } from '../../types/charts/chart-settings';
import { getPreviewDataAndCalculateGrandTotal } from '../../helpers/chart.helper';
import {
	populateDroppedFactsByDataframe,
	populateDroppedRowsByDataframe,
	setAvailableMeasuresAndDimensionsByDataframe,
	getEffectiveStaticFilters,
	mapDashboardFiltersForOverride,
} from '../../hooks/DataBuilderHooks';
import { AxiosError } from 'axios';
import { useMount } from 'react-use';
import { DataframeDataJoin } from '../../api/dataframe-data-join/DataframesDataJoin';
import { PeriodTypesEnum } from '../../enums/period-types-enum';
import { timestampToMMDDYYYY } from '../../utilities';
import { DataBuilderTypes } from '../../enums/data-builder-types';
import { InsightsActions } from '../../api/lambda/InsightsActions';
import { OptionsBuilderItemTypes } from '../../types/dataframes/options-builder-item-types';
import { useCommonStoreContext } from '../common-store/CommonStoreProvider';
import { DashboardFilterStore } from '../../types/dashboards/dashboard-filter-store';
import { Widget } from '../../api/dashbboards/DashboardWidgets';
import { TDashboardInightsEntity } from '../../api/shared-entity/SharedEntity';
import { summarizeDynamicData } from '../../helpers/insights-statistics-calculator';
import { getExtendedPrompt } from '../../api/zone/getExtendedPrompt';
import { DraggableMenuItemData } from '../../types/databuilder/databuilder';

type Props = {
	widgetId?: number;
	chartId: string;
	selectedDate?: TNewDateRange;
	allowClickNavigate?: boolean;
	height?: number | string;
	width?: number | string;
	filters?: DashboardFilter[];
	isEdit?: boolean;
	hideXAxis?: boolean;
	hideTitle?: boolean;
	transparentBackground: boolean;
	delayedDisplayTime?: number;
	hideYAxis?: boolean;
	fetchInsights?: boolean;
	setInsightsActions?: Dispatch<React.SetStateAction<InsightsActionsResponse | undefined>>;
	setHasRetrievalData?: Dispatch<React.SetStateAction<boolean>>;
	noAnimation?: boolean;
	selectedChartWidget?: Widget;
	insightEntities?: TDashboardInightsEntity[];
	// Defer loading on mount; call will be triggered by `triggerLoad` prop when true
	deferLoad?: boolean;
	// When true and deferLoad is set, the component will start fetching chart/data
	triggerLoad?: boolean;
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const retryWithBackoff = async <T,>(
	fn: () => Promise<T>,
	maxRetries = 3,
	baseDelayMs = 1000
): Promise<T> => {
	let lastError: Error | null = null;
	for (let attempt = 0; attempt < maxRetries; attempt++) {
		try {
			return await fn();
		} catch (error) {
			lastError = error as Error;
			if (attempt < maxRetries - 1) {
				const delayMs = baseDelayMs * Math.pow(2, attempt);
				console.log(`Retry attempt ${attempt + 1}/${maxRetries} after ${delayMs}ms`);
				await delay(delayMs);
			}
		}
	}
	throw lastError;
};

const ChartView = (props: Props) => {
	const {
		widgetId,
		chartId,
		selectedDate,
		allowClickNavigate,
		height,
		width,
		filters,
		isEdit,
		hideXAxis,
		hideTitle,
		transparentBackground,
		delayedDisplayTime,
		hideYAxis,
		noAnimation,
	} = props;
	const { addToast } = useToast();
	const { dashboardId, presentationId } = useParams();
	const { currentDatePeriods, dimensions, measures, unitTypes, zoneSettings } = useApplication();
	const defaultDatePeriod =
		currentDatePeriods.find((dp) => dp.period === 3) ?? (DateRange.Default() as TDateRange);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [chartLoaded, setChartLoaded] = useState<boolean>(false);
	const [chart, setChart] = useState<TChart | null>(null);

	const [chartSettings, setChartSettings] = useState<ZiChartSettings>({
		chartColor: '',
		hideLegend: true,
		hideYAxis: hideYAxis ?? false,
		limitResults: 0,
		showAverage: false,
		showDataValues: true,
		showPercentages: false,
		showScrollbar: false,
		showTargets: false,
		showValues: false,
		hideXAxis,
		hideTitle,
		overrideValueDefinedColors: false,
	});

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const [chartRetrevialData, setChartRetrevialData] = useState<any[]>([]);
	const [dimConfigValues, setDimConfigValues] = useState<DimSettings[]>([]);
	const [chartRetrevialCSVData, setChartRetrevialCSVData] =
		useState<MultipartResponse<DataframeDataRetrievalResponse>>();
	const [chartProps, setChartProps] = useState<ZiChartProps>({
		isConfigMode: false,
		displayChart: true,
		title: '',
		categoryTitle: '',
		valueTitle: '',
		chartRetrevialData: chartRetrevialData ?? [],
		dimConfigValues,
		chartData: chart ?? undefined,
		transparentBackground: transparentBackground,
		...(height && width && { size: { height, width } }),
		containerProps: { style: { height: '100%' } },
		chartSettings,
		setChartSettings,
		showLimitWarning: false,
		grandTotal: [{}],
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		chartRenderedCallback: () => {},
		isChartOnly: true,
		actualHumanifyChartNumbers: false,
		actualToggleChartYAxisOpposite: false,
		actualToggleChartXAxisOpposite: false,
		selectedLegendAlignment: 'center',
		selectedLegendVerticalAlignment: 'bottom',
	});

	const [dataframe, setDataframe] = useState<TDataframe>();
	const [parentDataframe, setParentDataframe] = useState<TDataframe>();
	const [dataframeHasJoins, setDataframeHasJoins] = useState<boolean>();
	const [hiddenClass, setHiddenClass] = useState<string>('hidden');
	const [unitType, setUnitType] = useState<TUnitType>();
	const [unitType2, setUnitType2] = useState<TUnitType>();
	const [numDecimals, setNumDecimals] = useState<number | undefined>(2);
	const [numDecimals2, setNumDecimals2] = useState<number | undefined>(2);
	const previousSelectedStartPeriod = localStorage.getItem('currentSelectedStartPeriod');
	const previousSelectedEndPeriod = localStorage.getItem('currentSelectedEndPeriod');
	const hasPreviouslySelectedPeriod =
		previousSelectedStartPeriod != null || previousSelectedEndPeriod != null;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const previouslySelectedStartDate: TDateRange | null =
		hasPreviouslySelectedPeriod && previousSelectedStartPeriod
			? JSON.parse(previousSelectedStartPeriod)
			: null;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const previouslySelectedEndDate: TDateRange | null =
		hasPreviouslySelectedPeriod && previousSelectedEndPeriod
			? JSON.parse(previousSelectedEndPeriod)
			: null;
	const currentUser = useUser();
	const { sharedDashboardFilters, setSharedDashboardFilters, setCurrentGraphSummaryStats } =
		useCommonStoreContext();
	const [dataframeIds, setDataframeIds] = useState<number[]>([]);

	const getChartLink = () => {
		let url = `/analyze/charts/view/chart/${chartId}`;
		if (dashboardId) {
			url = `/analyze/dashboards/${dashboardId}/chart/${chartId}/view`;
		} else if (presentationId) {
			url = `/present/${presentationId}/chart/${chartId}/view`;
		}

		return url;
	};

	useMount(() => {
		// Only auto-load chart on mount when not deferring load
		if (!props.deferLoad) {
			getChart();
		}
	});

	// If deferred loading is enabled, start loading when triggerLoad becomes true
	React.useEffect(() => {
		if (props.deferLoad && props.triggerLoad && !chart) {
			getChart();
		}
	}, [props.triggerLoad]);

	useEffect(() => {
		if (!chart || !dataframe) return;

		setIsLoading(true);
		setChartLoaded(false);
		getRetrievalData();
		setSharedDashboardFilters(mapFiltersToShare());
	}, [selectedDate, filters, dataframe?.id, JSON.stringify(dataframe?.filters)]);

	useEffect(() => {
		if (chart) {
			getChartDataframe();

			//delay wordcloud on dashboard for highcharts render
			if (chart.chart_type == 'wordcloud' && dashboardId) {
				setChartProps((prev) => ({
					...prev,
					chartRenderedCallback: () => {
						setTimeout(() => {
							setChartLoaded(true);
						}, 1500);
					},
				}));
			} else {
				setChartProps((prev) => ({
					...prev,
					chartRenderedCallback: () => {
						setChartLoaded(true);
					},
				}));
			}
		}
	}, [chart]);

	useEffect(() => {
		if (!dashboardId || !chart) {
			return;
		}

		const refreshDataframe = () => {
			getChartDataframe();
		};

		window.addEventListener('focus', refreshDataframe);
		return () => {
			window.removeEventListener('focus', refreshDataframe);
		};
	}, [dashboardId, chart?.id]);

	useEffect(() => {
		if (dataframe) {
			getDataframeJoins();
			getDataframeUnitType();
			getDataframeDecimalPrecision();
		}
	}, [dataframe]);

	useEffect(() => {
		if (dataframe && chartRetrevialData && chart) {
			const previewDataAndGrandTotal = getPreviewDataAndCalculateGrandTotal(
				populateDroppedFactsByDataframe(dataframe, measures),
				chartRetrevialData,
				true
			);

			const dimsToSet: DraggableMenuItemData[] = populateDroppedRowsByDataframe(
				dataframe,
				dimensions
			);
			const isSingleSeries =
				dimsToSet.filter((field) => field?.data?.isVisible).length > 1 ? false : true;

			const shouldbeTreemapStripesChartType =
				chart.chart_type == 'treemap' && !isSingleSeries;

			// Time out used to prevent the flash of the chart component before all data has initialized
			if (delayedDisplayTime) {
				setTimeout(() => {
					setHiddenClass('');
				}, delayedDisplayTime);
			} else {
				setHiddenClass('');
			}

			setIsLoading(false);

			if (!chartRetrevialData.length) {
				setChartLoaded(true);
			}
		}
	}, [chartRetrevialData]);

	useEffect(() => {
		if (props.fetchInsights && props.setInsightsActions) {
			getInsightsData();
		}
	}, [props.fetchInsights, props.setInsightsActions]);

	const getDataframeJoins = () => {
		if (dataframe?.parent) {
			void DataframeDataJoin.Get(dataframe.parent).then((response) => {
				setDataframeHasJoins(response.length ? true : false);
			});
		} else {
			setDataframeHasJoins(false);
		}
	};

	const getDataframeUnitType = () => {
		if (dataframe?.datasets[0] && dataframe.datasets[0].unitType) {
			const unitType1 = unitTypes?.find((x) => x.id == dataframe.datasets[0].unitType);
			setUnitType(unitType1);
		}

		if (dataframe?.datasets[1] && dataframe.datasets[1].unitType) {
			const unitType2 = unitTypes?.find((x) => x.id == dataframe.datasets[1].unitType);
			setUnitType2(unitType2);
		}
	};

	const getDataframeDecimalPrecision = () => {
		if (dataframe?.datasets[0]) {
			setNumDecimals(dataframe.datasets[0].numDecimals ?? chart?.num_decimals);
		}

		if (dataframe?.datasets[1]) {
			setNumDecimals2(dataframe.datasets[1].numDecimals ?? 2);
		}
	};

	const getRetrievalData = () => {
		if (!dataframe) return;

		const staticFilters = getEffectiveStaticFilters(dataframe, parentDataframe ?? undefined);
		const filterCacheKey = JSON.stringify({
			filters: filters ?? [],
			selectedDate,
			staticFilters,
		});

		// Check session cache first to avoid re-requesting if previously loaded this session
		if (props.deferLoad) {
			try {
				const cached = sessionStorage.getItem(`chart_data_${chartId}`)?.toString();
				if (cached) {
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					const parsed = JSON.parse(cached);

					if (
						parsed &&
						// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
						parsed.json &&
						// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
						parsed.csvData &&
						// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
						parsed.filterCacheKey === filterCacheKey
					) {
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument
						setChartRetrevialCSVData(parsed);
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						const newResponseRetrevialData = formatCsvDataToColumns(
							// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
							parsed.json?.columns ?? [],
							// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
							parsed.csvData ?? [],
							// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
							selectedDate?.period ?? defaultDatePeriod.period
						);
						setChartRetrevialData(newResponseRetrevialData);
						// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
						props.setHasRetrievalData?.(!!parsed.csvData);
						setIsLoading(false);
						return;
					}
				}
			} catch (e) {
				// ignore cache parse errors and continue to fetch
			}
		}

		const previousDate = {
			begin_date:
				hasPreviouslySelectedPeriod && previouslySelectedStartDate
					? previouslySelectedStartDate.begin_date
					: defaultDatePeriod?.begin_date ?? 0,
			end_date:
				hasPreviouslySelectedPeriod && previouslySelectedEndDate
					? previouslySelectedEndDate.end_date
					: defaultDatePeriod?.end_date ?? 0,
			period:
				hasPreviouslySelectedPeriod && previouslySelectedStartDate
					? previouslySelectedStartDate.period
					: defaultDatePeriod?.period ?? 0,
		};

		const dateToUse = selectedDate ?? previousDate;

		//manually update datqasets keyMeasureFact to null when undefined - BE issue
		const override: TDataframe | TNewDataframe = {
			id: dataframe.id,
			...(dataframe.parent && { parent: dataframe.parent }),
			columnEntry: [],
			name: 'override',
			rowEntry: dataframe.rowEntry,
			order: dataframe.order,
			filters: mapDashboardFiltersForOverride(
				filters ?? [],
				dataframe,
				parentDataframe ?? undefined,
				measures,
				dimensions
			),
			datasets: dataframe.datasets.map((x) => ({
				...x,
				keyMeasureFact: x.keyMeasureFact ? x.keyMeasureFact : x.id ?? null,
			})) as TNewDataframeData[],
		};

		/** -------------------------------
		 *  BUILD REQUEST
		 *  ------------------------------- */
		const request: DataframeDataRetrievalRequest = {
			dataframeId: dataframe.id,
			begin_date: dateToUse.begin_date,
			end_date: dateToUse.end_date,
			periodId: dateToUse.period,
			override,
		};

		setDataframeIds((prev) => [...prev, dataframe.id]);

		Dataframe.Retrieve(request)
			.then((newDataResponse) => {
				const newResponseRetrevialData = formatCsvDataToColumns(
					newDataResponse.json?.columns ?? [],
					newDataResponse.csvData ?? [],
					dateToUse.period ?? 3
				);

				if (dashboardId && newResponseRetrevialData.length > 0) {
					const summaryStatsdata = summarizeDynamicData(
						newResponseRetrevialData,
						chart?.name ?? '',
						chart?.id ?? 0
					);

					setCurrentGraphSummaryStats((prev) => [...prev, summaryStatsdata]);
				}

				props.setHasRetrievalData?.(!!newDataResponse.csvData);

				setChartRetrevialData(newResponseRetrevialData);
				setDimConfigValues(newDataResponse.json?.dim_setting ?? []);
				setChartRetrevialCSVData(newDataResponse);
				// Cache retrieval results for this session to avoid re-fetching if component unmounts
				if (props.deferLoad) {
					try {
						sessionStorage.setItem(
							`chart_data_${chartId}`,
							JSON.stringify({
								json: newDataResponse.json,
								csvData: newDataResponse.csvData,
								filterCacheKey,
							})
						);
					} catch (e) {
						// ignore storage errors
					}
				}
				setIsLoading(false);
			})
			.catch((e: AxiosError<{ message: string }>) => {
				addToast(e.response?.data?.message ?? 'Error fetching data.', AlertVariant.danger);
				setIsLoading(false);
				setChartLoaded(true);
			});
	};

	const getChartDataframe = () => {
		if (!chart?.dataframe) {
			return;
		}

		const dataframeId = chart.dataframe as number;

		Dataframe.Get(dataframeId, ['datasets', 'filters', 'rowEntry', 'order'], {
			bypassCache: true,
		})
			.then((dataframeResponse) => {
				if (dataframeResponse.parent) {
					Dataframe.Get(
						dataframeResponse.parent,
						['datasets', 'filters', 'rowEntry', 'order'],
						{ bypassCache: true }
					)
						.then((dataframeParentResponse) => {
							setDataframe(dataframeResponse);
							setParentDataframe(dataframeParentResponse);
						})
						.catch(() => {
							setIsLoading(false);
							addToast(
								'There was an issue retrieving the parent dataframe',
								AlertVariant.danger
							);
						});
				} else {
					setDataframe(dataframeResponse);
					setParentDataframe(undefined);
				}
			})
			.catch(() => {
				setIsLoading(false);
				addToast('There was an issue retrieving the dataframe', AlertVariant.danger);
			});
	};

	const getChart = () => {
		if (chartId == '0') {
			setIsLoading(false);
			return;
		}

		// Attempt to hydrate chart metadata from session cache (only for deferred discover loads)
		if (props.deferLoad) {
			try {
				const cachedMeta = sessionStorage.getItem(`chart_meta_${chartId}`);
				if (cachedMeta) {
					const chartResponse = JSON.parse(cachedMeta) as TChart;
					if (chartResponse) {
						setChart(chartResponse);
						setChartSettings({
							chartColor: chartResponse.color_set,
							hideLegend: chartResponse.hide_legend,
							hideYAxis: hideYAxis ?? chartResponse.hide_y_axis,
							limitResults: 500,
							showAverage: chartResponse.show_average,
							showDataValues: chartResponse.show_data_values,
							showPercentages: chartResponse.show_percentages,
							showScrollbar: chartResponse.show_scrollbar,
							showTargets: chartResponse.show_targets,
							showValues: chartResponse.show_targets,
							hideXAxis,
							hideTitle,
							overrideValueDefinedColors: chartResponse.override_value_defined_colors,
						});
						setChartProps((prev) => ({
							...prev,
							actualHumanifyChartNumbers:
								chartResponse.humanify_chart_numbers ?? false,
							actualToggleChartXAxisOpposite:
								chartResponse.toggle_x_axis_opposite ?? false,
							actualToggleChartYAxisOpposite:
								chartResponse.toggle_y_axis_opposite ?? false,
							selectedLegendAlignment: chartResponse.legend_alignment ?? 'center',
							selectedLegendVerticalAlignment:
								chartResponse.legend_vertical_alignment ?? 'bottom',
						}));
						setIsLoading(false);
						return;
					}
				}
			} catch (e) {
				// ignore cache parse errors and continue to fetch remote
			}
		}

		setIsLoading(true);
		Chart.GetOne(parseInt(chartId))
			.then((chartResponse: TChart): void => {
				setChart(chartResponse);
				// cache chart meta in session for the session lifetime (only for deferred discover loads)
				if (props.deferLoad) {
					try {
						sessionStorage.setItem(
							`chart_meta_${chartId}`,
							JSON.stringify(chartResponse)
						);
					} catch (e) {
						// ignore storage errors
					}
				}
				setChartSettings({
					chartColor: chartResponse.color_set,
					hideLegend: chartResponse.hide_legend,
					hideYAxis: hideYAxis ?? chartResponse.hide_y_axis,
					limitResults: 500,
					showAverage: chartResponse.show_average,
					showDataValues: chartResponse.show_data_values,
					showPercentages: chartResponse.show_percentages,
					showScrollbar: chartResponse.show_scrollbar,
					showTargets: chartResponse.show_targets,
					showValues: chartResponse.show_targets,
					hideXAxis,
					hideTitle,
					overrideValueDefinedColors: chartResponse.override_value_defined_colors,
				});
				setChartProps((prev) => ({
					...prev,
					actualHumanifyChartNumbers: chartResponse.humanify_chart_numbers ?? false,
					actualToggleChartXAxisOpposite: chartResponse.toggle_x_axis_opposite ?? false,
					actualToggleChartYAxisOpposite: chartResponse.toggle_y_axis_opposite ?? false,
					selectedLegendAlignment: chartResponse.legend_alignment ?? 'center',
					selectedLegendVerticalAlignment:
						chartResponse.legend_vertical_alignment ?? 'bottom',
				}));
				setIsLoading(false);
			})
			.catch((): void => {
				addToast('Error fetching dataframe chart data.', AlertVariant.danger);
				setIsLoading(false);
			})
			.catch((): void => {
				addToast('Error fetching chart data.', AlertVariant.danger);
				setIsLoading(false);
			});
	};

	const getInsightsData = () => {
		if (dataframe && chartRetrevialCSVData) {
			const previousDate = {
				begin_date:
					hasPreviouslySelectedPeriod && previouslySelectedStartDate
						? previouslySelectedStartDate?.begin_date
						: defaultDatePeriod?.begin_date ?? 0,
				end_date:
					hasPreviouslySelectedPeriod && previouslySelectedEndDate
						? previouslySelectedEndDate.end_date
						: defaultDatePeriod?.end_date ?? 0,
				period:
					hasPreviouslySelectedPeriod && previouslySelectedStartDate
						? previouslySelectedStartDate.period
						: defaultDatePeriod?.period ?? 0,
			};

			const date = selectedDate ?? previousDate;

			const request: InsightsActionsRequest = {
				metadata: {
					key_measures: dataframe?.datasets.map((fact) => {
						return {
							name: fact.title ?? '',
							description: '',
						};
					}),
					dimensions: dataframe?.rowEntry.map((dim) => {
						return {
							name: dim.title ?? '',
							description: '',
						};
					}),
					columns: chartRetrevialCSVData.json?.columns ?? [],
					...getPeriodData(date),
					filters_applied: getInsightsFilters(),
					extended_prompt:
						[getExtendedPrompt(zoneSettings), chart?.ai_metadetails]
							.filter(Boolean)
							.join('\n\n') || undefined,
				},
				data: chartRetrevialCSVData.csvDataUnformatted ?? '',
				logging: {
					user_id: currentUser.id,
					entity_type: DataBuilderTypes.chart,
					entity_id: chart?.id ?? 0,
				},
			};

			retryWithBackoff(() => InsightsActions.GetInsights(request), 3, 1000)
				.then((response) => {
					if (props.setInsightsActions) {
						if (response.success) {
							if (typeof response.data === 'string') {
								response.data = JSON.parse(
									response.data
								) as InsightsActionsResponse;
							}
							props.setInsightsActions(response.data);
						} else {
							addToast(response.message, AlertVariant.danger);
							props.setInsightsActions(undefined);
						}
					}
				})
				.catch((): void => {
					addToast('Error fetching insights after retries.', AlertVariant.danger);
					props.setInsightsActions && props.setInsightsActions(undefined);
				});
		}
	};

	const getInsightsFilters = () => {
		const filters: string[] = [];
		if (dataframe) {
			dataframe.filters.forEach((filter) => {
				if (filter.entity_type === OptionsBuilderItemTypes.DimensionAttribute) {
					const dim = dimensions.find((dim) =>
						dim.dimensionAttributes.some((dimAttr) => dimAttr.id === filter.entity_id)
					);
					if (dim) {
						const dimAttr = dim.dimensionAttributes.find(
							(dimAttr) => dimAttr.id === filter.entity_id
						);

						if (dimAttr) {
							filters.push(`${dimAttr.name ?? ''}: ${filter.value ?? ''}`);
						}
					}
				} else if (filter.entity_type === OptionsBuilderItemTypes.Dimension) {
					const dim = dimensions.find((dim) => dim.id === filter.entity_id);

					if (dim) {
						filters.push(`${dim.name ?? ''}: ${filter.value ?? ''}`);
					}
				}
			});
		}

		return filters;
	};

	const getPeriodData = (date: { begin_date: number; end_date: number; period: number }) => {
		const response = {
			time_period: '',
			time_range: {
				start_date: '',
				end_date: '',
			},
		};

		if (date) {
			response.time_range.start_date = timestampToMMDDYYYY(date.begin_date);
			response.time_range.end_date = timestampToMMDDYYYY(date.end_date);

			switch (date.period) {
				case 1: {
					response.time_period = PeriodTypesEnum.Year;
					break;
				}
				case 2: {
					response.time_period = PeriodTypesEnum.Quarter;
					break;
				}
				case 3: {
					response.time_period = PeriodTypesEnum.Month;
					break;
				}
				case 4: {
					response.time_period = PeriodTypesEnum.Week;
					break;
				}
				default: {
					response.time_period = PeriodTypesEnum.Month;
					break;
				}
			}
		}

		return response;
	};

	const mapFiltersToShare = (): DashboardFilterStore[] => {
		if (dashboardId && widgetId) {
			const availableItems = setAvailableMeasuresAndDimensionsByDataframe(
				dataframe,
				parentDataframe ?? undefined,
				measures,
				dimensions,
				false
			);
			const existingSharedFilter = sharedDashboardFilters.find(
				(filter) => filter.id === +dashboardId
			);

			const allowedFilters = filters
				? filters.filter((item) => {
						if (item.entity_type === OptionsBuilderItemTypes.Dimension) {
							const dimIds = availableItems.dimensions.map((dim) => dim.id);
							return item.value && dimIds.includes(item.entity_id);
						} else {
							const attributes = availableItems.dimensions.flatMap(
								(dim) => dim.dimensionAttributes
							);
							const attributeIds = attributes.map((attr) => attr.id);
							return item.value && attributeIds.includes(item.entity_id);
						}
				  })
				: [];

			if (existingSharedFilter) {
				const widgetExists = existingSharedFilter.widgets.find(
					(widget) => widget.id === widgetId
				);

				return sharedDashboardFilters.map((filter) => {
					if (filter.id === +dashboardId) {
						if (widgetExists) {
							filter.widgets = filter.widgets.map((widget) => {
								if (widget.id === widgetId) {
									widget.filters = allowedFilters;
								}
								return widget;
							});
						} else {
							filter.widgets = [
								...filter.widgets,
								...[
									{
										id: widgetId,
										itemId: +chartId,
										filters: allowedFilters,
									},
								],
							];
						}
					}

					return filter;
				});
			} else {
				return [
					...(sharedDashboardFilters ?? []),
					...[
						{
							id: +dashboardId,
							widgets: [
								{
									id: widgetId,
									itemId: +chartId,
									filters: allowedFilters,
								},
							],
						},
					],
				];
			}
		}

		return sharedDashboardFilters;
	};

	const componentStyles = {
		height: '100%',
		width: '100%',
		overflow: 'hidden',
		display: chartLoaded ? 'block' : 'none',
	};

	// Memoize derived chart props values
	const derivedChartProps = React.useMemo(() => {
		if (!chart || !dataframe) return null;

		const dimsToSet = populateDroppedRowsByDataframe(dataframe, dimensions);
		const isSingleSeries = dimsToSet.filter((field) => field?.data?.isVisible).length <= 1;
		const shouldBeTreemapStripes = chart.chart_type === 'treemap' && !isSingleSeries;

		const previewDataAndGrandTotal = getPreviewDataAndCalculateGrandTotal(
			populateDroppedFactsByDataframe(dataframe, measures),
			chartRetrevialData,
			true
		);

		return {
			...chartProps,
			chartRetrevialData: chartRetrevialData || [],
			chartType: shouldBeTreemapStripes
				? 'treemapstripes'
				: (chart.chart_type as ChartSeriesChartTypes),
			secondChartType: chart.second_series_chart_type as ChartSeriesChartTypes,
			chartData: chart,
			title: chart.name,
			selectedNumDecimals: numDecimals,
			selected2ndNumDecimals: numDecimals2,
			...(unitType && {
				selectedUnitType: unitType,
				unitType: unitType,
				valueTitle: unitType.name,
			}),
			...(unitType2 && { selected2ndUnitType: unitType2 }),
			chartSettings,
			showLimitWarning: false,
			grandTotal: previewDataAndGrandTotal?.grandTotal?.length
				? [previewDataAndGrandTotal.grandTotal[0]] // <-- TS-safe single element
				: [{}], // fallback
			droppedFacts: populateDroppedFactsByDataframe(dataframe, measures),
			droppedFields: dimsToSet,
			unitTypes,
			noAnimation,
			dimConfigValues,
			isSingleSeries,
		} as ZiChartProps;
	}, [
		chart,
		dataframe,
		chartRetrevialData,
		numDecimals,
		numDecimals2,
		unitType,
		unitType2,
		chartSettings,
		dimConfigValues,
		noAnimation,
		measures,
		dimensions,
		unitTypes,
	]);

	// Single effect to update chartProps
	useEffect(() => {
		if (derivedChartProps) {
			setChartProps(derivedChartProps);
			setHiddenClass(''); // show chart only after props ready
			setIsLoading(false);

			// Set chartLoaded only if there's no data (otherwise ZiChart callback handles it)
			if (!chartRetrevialData?.length) {
				setChartLoaded(true);
			}
		}
	}, [derivedChartProps]);

	let component = <Loader />;

	if (!isLoading) {
		component = (
			<div
				style={componentStyles}
				className={`${hiddenClass} ${
					props.selectedChartWidget?.id == widgetId && dashboardId
						? 'selected-chart-widget-border'
						: ''
				}`}
				id={`widget-${widgetId ?? 0}`}
			>
				<ZiChart
					{...chartProps}
					chartRetrevialData={chartRetrevialData || []}
				/>
			</div>
		);

		if (allowClickNavigate && !isEdit) {
			component = (
				<div
					style={componentStyles}
					className={hiddenClass}
				>
					<Link
						to={getChartLink()}
						onClick={() => {
							setSharedDashboardFilters(mapFiltersToShare());
						}}
					>
						{component}
					</Link>
				</div>
			);
		}
	}
	return component;
};

export default ChartView;
